import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';

export interface SidebarMenuItem {
  label: string;
  icon?: string;
  route?: string;
  href?: string;
  active?: boolean;
  children?: SidebarMenuItem[];
  safeIcon?: SafeHtml;
}

export interface SidebarCallToAction {
  title: string;
  description: string;
  linkText: string;
  linkRoute?: string;
  linkHref?: string;
  icon?: string;
  safeIcon?: SafeHtml;
}

export interface SidebarFooterItem {
  label: string;
  icon?: string;
  route?: string;
  href?: string;
  safeIcon?: SafeHtml;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  private _menuItems: SidebarMenuItem[] = [];

  @Input() set menuItems(items: SidebarMenuItem[]) {
    this._menuItems = (items || []).map((item) => ({
      ...item,
      safeIcon: item.icon ? this.sanitizer.bypassSecurityTrustHtml(item.icon) : undefined,
    }));
  }

  get menuItems(): SidebarMenuItem[] {
    return this._menuItems;
  }

  @Input() title: string = 'Menu';
  
  private _callToAction?: SidebarCallToAction;
  @Input() set callToAction(value: SidebarCallToAction | undefined) {
    if (value) {
      this._callToAction = {
        ...value,
        safeIcon: value.icon ? this.sanitizer.bypassSecurityTrustHtml(value.icon) : undefined,
      };
    } else {
      this._callToAction = undefined;
    }
  }
  get callToAction(): SidebarCallToAction | undefined {
    return this._callToAction;
  }

  private _footerItems: SidebarFooterItem[] = [];
  @Input() set footerItems(items: SidebarFooterItem[] | undefined) {
    this._footerItems = (items || []).map((item) => ({
      ...item,
      safeIcon: item.icon ? this.sanitizer.bypassSecurityTrustHtml(item.icon) : undefined,
    }));
  }
  get footerItems(): SidebarFooterItem[] {
    return this._footerItems;
  }

  collapsed = false;
  offcanvasOpen = false;
  dropdownOpen = false;
  currentPageLabel = 'Dashboard';
  openDropdowns: Set<string> = new Set();

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Get initial page label and open relevant dropdowns
    this.updateCurrentPageLabel();
    this.updateOpenDropdowns();
    
    // Listen to route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateCurrentPageLabel();
        this.updateOpenDropdowns();
      });
  }

  updateOpenDropdowns(): void {
    const currentRoute = this.router.url.split('?')[0].split('#')[0]; // Remove query params and fragments
    
    // Clear all dropdowns first
    this.openDropdowns.clear();
    
    // Check menu items and their children
    for (const item of this.menuItems) {
      // Check if current route matches any child route
      if (item.children && item.children.length > 0) {
        const activeChild = item.children.find(child => {
          if (!child.route) return false;
          const childRoute = child.route.split('?')[0].split('#')[0]; // Remove query params and fragments
          // Check if current route exactly matches or starts with the child route
          // This handles nested routes like /add-dataset/uploads matching /add-dataset
          return currentRoute === childRoute || 
                 currentRoute.startsWith(childRoute + '/');
        });
        if (activeChild) {
          // Keep dropdown open if we're on a child page
          this.openDropdowns.add(item.label);
        }
      }
    }
  }

  updateCurrentPageLabel(): void {
    const currentRoute = this.router.url.split('?')[0].split('#')[0]; // Remove query params and fragments
    
    // Check menu items and their children
    for (const item of this.menuItems) {
      // Check if current route matches item route
      if (item.route) {
        const itemRoute = item.route.split('?')[0].split('#')[0];
        if (currentRoute === itemRoute || currentRoute.startsWith(itemRoute + '/')) {
          this.currentPageLabel = item.label;
          return;
        }
      }
      
      // Check children - prioritize exact matches, then prefix matches
      if (item.children && item.children.length > 0) {
        // First try exact match
        let activeChild = item.children.find(child => {
          if (!child.route) return false;
          const childRoute = child.route.split('?')[0].split('#')[0];
          return currentRoute === childRoute;
        });
        
        // If no exact match, try prefix match (for nested routes)
        if (!activeChild) {
          activeChild = item.children.find(child => {
            if (!child.route) return false;
            const childRoute = child.route.split('?')[0].split('#')[0];
            return currentRoute.startsWith(childRoute + '/');
          });
        }
        
        if (activeChild) {
          this.currentPageLabel = activeChild.label;
          return;
        }
      }
    }
    
    // Check footer items
    if (currentRoute.includes('/profile')) {
      this.currentPageLabel = 'Profile';
    } else if (currentRoute.includes('/support')) {
      this.currentPageLabel = 'Support';
    } else {
      this.currentPageLabel = 'Dashboard';
    }
  }

  handleMenuClick(item: SidebarMenuItem, event?: Event): void {
    // Prevent event bubbling if provided (but allow default navigation)
    if (event) {
      event.stopPropagation();
    }
    
    // Update current page label
    this.currentPageLabel = item.label;
    
    // Ensure dropdown is open if this is a child item (do this before navigation)
    if (item.route) {
      // Find the parent item that contains this child
      for (const menuItem of this.menuItems) {
        if (menuItem.children && menuItem.children.some(child => child.route === item.route)) {
          // Open the dropdown before navigation to ensure it's visible
          if (!this.openDropdowns.has(menuItem.label)) {
            this.openDropdowns.add(menuItem.label);
          }
          break;
        }
      }
    }
    
    // Close offcanvas on mobile/tablet
    const isMobileTablet = window.innerWidth < 992;
    if (isMobileTablet) {
      this.closeOffcanvas();
    }
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
  }

  toggleOffcanvas(): void {
    this.offcanvasOpen = !this.offcanvasOpen;
  }

  closeOffcanvas(): void {
    this.offcanvasOpen = false;
  }

  isOrganisationRouteActive(): boolean {
    const currentRoute = this.router.url;
    return currentRoute.includes('/join-organisation') || currentRoute.includes('/create-organisation');
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  toggleItemDropdown(itemLabel: string, event?: Event): void {
    // Prevent event from bubbling if provided
    if (event) {
      event.stopPropagation();
    }
    
    if (this.openDropdowns.has(itemLabel)) {
      // If clicking the same dropdown, close it
      this.openDropdowns.delete(itemLabel);
    } else {
      // Close all other dropdowns first, then open the clicked one
      this.openDropdowns.clear();
      this.openDropdowns.add(itemLabel);
    }
  }

  isDropdownOpen(itemLabel: string): boolean {
    return this.openDropdowns.has(itemLabel);
  }

  openOffcanvasFromDropdown(item: SidebarMenuItem): void {
    // Only open offcanvas on mobile/tablet (when dropdown is visible)
    // Check if we're on mobile/tablet by checking window width
    const isMobileTablet = window.innerWidth < 992; // lg breakpoint
    
    if (isMobileTablet) {
      // On mobile/tablet: open offcanvas sidebar
      this.dropdownOpen = false;
      this.offcanvasOpen = true;
    } else {
      // On desktop: just close dropdown, navigation will happen via routerLink
      this.dropdownOpen = false;
    }
  }
}
